import { SortableHeaderCell } from '@qodo/design-system';
import { Column } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { TeamData } from '../../network/httpClients/PlatformClient/api.schema';
import { spawn } from '../../utils/translations';
import { TOOLTIP_SUFFIX } from './consts';
import { TeamsTableData } from './types';

/**
 * Normalizes team data for table display
 * @param team - Raw team data from API
 * @returns Normalized team data for table
 */
export const normalizeTeamData = (team: TeamData): TeamsTableData => {
  return {
    id: team.id,
    name: team.name,
    created: team.dateCreated,
    owner: team.ownerUserId,
    membersCount: team.users?.length || 0,
  };
};

/**
 * Renders a sortable header cell with the translated column text
 * @param column - The TanStack table column
 * @param id - The column ID for translation key
 * @returns SortableHeaderCell component
 */
export const renderSortableHeaderCell = (
  column: Column<TeamsTableData, unknown>,
  id: string,
  showTooltip: boolean = false,
) => {
  const spawnColumns = spawn('teams.table.columns');
  const tooltipContent = showTooltip
    ? spawnColumns(id + TOOLTIP_SUFFIX)
    : undefined;

  return (
    <SortableHeaderCell column={column} tooltipContent={tooltipContent}>
      {spawnColumns(id)}
    </SortableHeaderCell>
  );
};

/**
 * Checks if an error should be ignored (not show toast or retry)
 * Currently checks for 403 Forbidden errors which should show empty state instead
 * @param error - The AxiosError to check
 * @returns true if the error should be ignored, false otherwise
 */
export const isIgnorableError = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.response?.status === StatusCodes.FORBIDDEN;
  }

  return false;
};

/**
 * Gets the display text for owner cell based on owner value
 * @param owner - Single owner string or array of owner strings
 * @returns Display text for the owner cell (e.g., "owner1" or "owner1 (+2)")
 */
export const getOwnerDisplayText = (owner: string | string[]): string => {
  if (Array.isArray(owner)) {
    switch (owner.length) {
      case 0:
        return '';
      case 1:
        return owner[0];

      default: {
        const additionalCount = owner.length - 1;

        return `${owner[0]} (+${additionalCount})`;
      }
    }
  }

  return owner;
};

/**
 * Gets the tooltip text for multiple owners
 * @param owner - Single owner string or array of owner strings
 * @returns Comma-separated list of all owners, or undefined if single owner
 */
export const getOwnerTooltipText = (
  owner: string | string[],
): string | undefined => {
  const shouldBuildTooltip = Array.isArray(owner) && owner.length > 1;

  if (!shouldBuildTooltip) {
    return undefined;
  }

  return owner.join(', ');
};

/**
 * Gets the first owner for sorting purposes
 * @param owner - Single owner string or array of owner strings
 * @returns First owner string for sorting
 */
export const getFirstOwner = (owner: string | string[]): string => {
  if (Array.isArray(owner)) {
    return owner[0] || '';
  }

  return owner;
};

/**
 * Checks if a search term matches any owner in the owner value
 * @param owner - Single owner string or array of owner strings
 * @param searchTerm - The search term to match
 * @returns true if any owner matches the search term
 */
export const doesOwnerMatchSearch = (
  owner: string | string[],
  searchTerm: string,
): boolean => {
  const lowerSearchTerm = searchTerm.toLowerCase();

  if (Array.isArray(owner)) {
    return owner.some((ownerValue) =>
      ownerValue.toLowerCase().includes(lowerSearchTerm),
    );
  }

  return owner.toLowerCase().includes(lowerSearchTerm);
};

/**
 * Converts owner(s) to a searchable string for global filter
 * @param owner - Single owner string or array of owner strings
 * @returns Searchable string representation of owner(s)
 */
export const ownersToSearchString = (owner: string | string[]): string => {
  if (Array.isArray(owner)) {
    return owner.join(' ');
  }

  return owner;
};
